import React, { useEffect, useState } from "react";
import { Save, UserPlus, Trash2, RefreshCw, Fingerprint, Loader2, Plus, Upload, ImageIcon, PenLine, Info, RotateCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCompany } from "@/lib/company";
import { isPasskeySupported, listPasskeys, registerPasskey, deletePasskey } from "@/lib/passkey";

// ============================================================
// Approval Thresholds tab — owner-only
// ============================================================
function ApprovalsSettingsTab() {
  const { active } = useCompany();
  const cur = active?.base_currency || "INR";
  const [t, setT] = useState({ invoice: 50000, purchase_order: 100000, payment: 25000 });
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/approvals/thresholds")
      .then((r) => { setT(r.data.thresholds); setDefaults(r.data.defaults); })
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/approvals/thresholds", {
        invoice: Number(t.invoice),
        purchase_order: Number(t.purchase_order),
        payment: Number(t.payment),
      });
      setT(data.thresholds);
      toast.success("Approval limits updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="settings-approvals-tab">
      <div className="card-surface p-4 sm:p-6">
        <h3 className="font-semibold text-base mb-2">Approval limits ({cur})</h3>
        <p className="text-sm text-[#4B5563] mb-4">
          Documents at or above these amounts require Super Admin approval before they post to
          the ledger. Owners and Super Admin bypass the gate on their own transactions.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {["invoice", "purchase_order", "payment"].map((k) => (
            <div key={k}>
              <label className="label-field capitalize">{k.replace("_", " ")} limit</label>
              <input
                type="number" inputMode="decimal" className="input-clean"
                value={t[k]} onChange={(e) => setT({ ...t, [k]: e.target.value })}
                data-testid={`threshold-${k}`}
              />
              {defaults && <div className="text-[10px] text-[#94A3B8] font-mono-data mt-0.5">default {defaults[k]}</div>}
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-4">
          <button data-testid="save-thresholds-btn" onClick={save} disabled={saving} className="btn-primary w-full sm:w-auto justify-center">
            <Save size={14}/> {saving ? "Saving…" : "Save Limits"}
          </button>
        </div>
      </div>
    </div>
  );
}

const ROLES = ["owner", "admin", "accountant", "sales", "readonly"];

function CompanyTab() {
  const { active, refresh } = useCompany();
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const [stripeCfg, setStripeCfg] = useState(null);

  useEffect(() => { if (active) setF({ ...active }); }, [active]);
  useEffect(() => {
    api.get("/stripe/config").then((r) => setStripeCfg(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/companies/${active.id}`, f);
      toast.success("Company updated");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (!active) return null;
  return (
    <div className="card-surface p-6 space-y-4" data-testid="settings-company-tab">
      <h3 className="font-semibold">Company Profile</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className="label-field">Name</label>
          <input className="input-clean" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })}/></div>
        <div><label className="label-field">Country</label>
          <input className="input-clean" maxLength={2} value={f.country || ""} onChange={(e) => setF({ ...f, country: e.target.value.toUpperCase() })}/></div>
        <div><label className="label-field">Base Currency</label>
          <input className="input-clean font-mono-data uppercase" maxLength={3} value={f.base_currency || ""} onChange={(e) => setF({ ...f, base_currency: e.target.value.toUpperCase() })}/></div>
        <div><label className="label-field">Tax Regime</label>
          <select className="input-clean" value={f.tax_regime || "NONE"} onChange={(e) => setF({ ...f, tax_regime: e.target.value })}>
            <option>IN_GST</option><option>AE_VAT</option><option>EU_VAT</option><option>UK_VAT</option><option>US_SALES_TAX</option><option>NONE</option>
          </select></div>
        <div><label className="label-field">GSTIN</label>
          <input className="input-clean font-mono-data" value={f.gstin || ""} onChange={(e) => setF({ ...f, gstin: e.target.value.toUpperCase() })}/></div>
        <div><label className="label-field">VAT ID</label>
          <input className="input-clean font-mono-data" value={f.vat_id || ""} onChange={(e) => setF({ ...f, vat_id: e.target.value })}/></div>
        <div><label className="label-field">IEC Number (optional)</label>
          <input data-testid="company-iec" className="input-clean font-mono-data uppercase" value={f.iec_code || ""} onChange={(e) => setF({ ...f, iec_code: e.target.value.toUpperCase() })}/></div>
        <div><label className="label-field">State</label>
          <input className="input-clean" value={f.state || ""} onChange={(e) => setF({ ...f, state: e.target.value })}/></div>
        <div><label className="label-field">State Code</label>
          <input className="input-clean font-mono-data uppercase" maxLength={2} value={f.state_code || ""} onChange={(e) => setF({ ...f, state_code: e.target.value.toUpperCase() })}/></div>
        <div><label className="label-field">Invoice Prefix</label>
          <input className="input-clean font-mono-data" value={f.invoice_prefix || ""} onChange={(e) => setF({ ...f, invoice_prefix: e.target.value.toUpperCase() })}/></div>
        <div><label className="label-field">Fiscal Year Start Month</label>
          <input type="number" min={1} max={12} className="input-clean font-mono-data" value={f.fiscal_year_start_month || 4} onChange={(e) => setF({ ...f, fiscal_year_start_month: parseInt(e.target.value, 10) || 4 })}/></div>
      </div>
      <div className="mt-4 border-t border-[#E2E8F0] pt-4">
        <h4 className="font-semibold text-sm mb-1">Contact & Website</h4>
        <p className="text-xs text-[#64748B] mb-3">
          Rendered on every PDF header (phone ☎, email ✉, website ◉).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><label className="label-field">Phone</label>
            <input data-testid="company-phone" className="input-clean" value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })}/></div>
          <div><label className="label-field">Email</label>
            <input data-testid="company-email" type="email" className="input-clean" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })}/></div>
          <div><label className="label-field">Website</label>
            <input data-testid="company-website" className="input-clean" value={f.website || ""} onChange={(e) => setF({ ...f, website: e.target.value })}/></div>
        </div>
      </div>
      <div className="mt-4 border-t border-[#E2E8F0] pt-4">
        <h4 className="font-semibold text-sm mb-1">Bank / Remittance Details</h4>
        <p className="text-xs text-[#64748B] mb-3">
          Printed inside the <b>PAYMENT DETAILS</b> box at the bottom of every invoice.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="label-field">Bank Name</label>
            <input data-testid="company-bank-name" className="input-clean" value={f.bank_name || ""} onChange={(e) => setF({ ...f, bank_name: e.target.value })}/></div>
          <div><label className="label-field">Account Holder</label>
            <input className="input-clean" value={f.bank_account_name || ""} onChange={(e) => setF({ ...f, bank_account_name: e.target.value })}/></div>
          <div><label className="label-field">Account Number</label>
            <input className="input-clean font-mono-data" value={f.bank_account_number || ""} onChange={(e) => setF({ ...f, bank_account_number: e.target.value })}/></div>
          <div><label className="label-field">IFSC</label>
            <input className="input-clean font-mono-data uppercase" value={f.bank_ifsc || ""} onChange={(e) => setF({ ...f, bank_ifsc: e.target.value.toUpperCase() })}/></div>
          <div><label className="label-field">SWIFT / BIC</label>
            <input className="input-clean font-mono-data uppercase" value={f.bank_swift || ""} onChange={(e) => setF({ ...f, bank_swift: e.target.value.toUpperCase() })}/></div>
          <div><label className="label-field">UPI</label>
            <input className="input-clean font-mono-data" value={f.bank_upi || ""} onChange={(e) => setF({ ...f, bank_upi: e.target.value })}/></div>
        </div>
      </div>
      <div className="mt-4 border-t border-[#E2E8F0] pt-4">
        <h4 className="font-semibold text-sm mb-1">One-Click Automated Invoicing</h4>
        <p className="text-xs text-[#64748B] mb-3">
          When an invoice moves out of Draft, SERP can auto-deliver the branded
          PDF to the customer over Email (Resend) and WhatsApp (pre-filled wa.me link
          — or Twilio / Meta if configured).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="flex items-center gap-2 text-sm" data-testid="toggle-auto-email">
            <input type="checkbox"
              checked={f.auto_email_on_send !== false}
              onChange={(e) => setF({ ...f, auto_email_on_send: e.target.checked })}
            />
            Auto-email invoice PDF on send
          </label>
          <label className="flex items-center gap-2 text-sm" data-testid="toggle-auto-whatsapp">
            <input type="checkbox"
              checked={f.auto_whatsapp_on_send !== false}
              onChange={(e) => setF({ ...f, auto_whatsapp_on_send: e.target.checked })}
            />
            Auto-send WhatsApp notification on send
          </label>
          <label className="flex items-center gap-2 text-sm" data-testid="toggle-auto-stripe">
            <input type="checkbox"
              checked={f.auto_stripe_link_on_send === true}
              onChange={(e) => setF({ ...f, auto_stripe_link_on_send: e.target.checked })}
            />
            Auto-generate Stripe Pay-Now link on send
          </label>
        </div>
        {stripeCfg && (
          <div className="mt-3 text-xs" data-testid="stripe-mode-badge">
            <span className="text-[#64748B]">Stripe status:&nbsp;</span>
            {stripeCfg.mode === "live" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#DCFCE7] px-2 py-0.5 text-[#166534] font-semibold">
                ● LIVE mode
              </span>
            )}
            {stripeCfg.mode === "test" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#FEF3C7] px-2 py-0.5 text-[#92400E] font-semibold">
                ● TEST mode
              </span>
            )}
            {stripeCfg.mode === "unconfigured" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#FEE2E2] px-2 py-0.5 text-[#991B1B] font-semibold">
                ● NOT configured
              </span>
            )}
            {stripeCfg.mode === "custom" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#E0E7FF] px-2 py-0.5 text-[#3730A3] font-semibold">
                ● custom key
              </span>
            )}
            <span className="text-[#64748B] ml-2">
              Update <code className="font-mono-data">STRIPE_API_KEY</code> in Secrets to switch modes.
            </span>
          </div>
        )}
      </div>
      <div className="mt-4 border-t border-[#E2E8F0] pt-4" data-testid="chart-theme-card">
        <h4 className="font-semibold text-sm mb-1">Excel Chart Theme</h4>
        <p className="text-xs text-[#64748B] mb-3">
          Colour palette applied to the embedded bar charts on Ageing (Receivables / Payables)
          Excel exports. Change it to match the deck you send to your board.
        </p>
        <div className="flex gap-2 flex-wrap" role="radiogroup" aria-label="Excel Chart Theme">
          {[
            { key: "navy_gold", lbl: "Navy · Gold", swatch: ["#0F172A", "#B45309"] },
            { key: "mono",       lbl: "Monochrome", swatch: ["#475569", "#0F172A"] },
            { key: "pastel",     lbl: "Pastel",     swatch: ["#93C5FD", "#1E3A8A"] },
          ].map((t) => {
            const selected = (f.chart_theme || "navy_gold") === t.key;
            return (
            <button
              key={t.key}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-pressed={selected}
              data-testid={`chart-theme-${t.key}`}
              onClick={() => setF({ ...f, chart_theme: t.key })}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs font-semibold border transition ${
                selected
                  ? "border-[#0F172A] bg-[#0F172A] text-white"
                  : "border-[#E2E8F0] bg-white text-[#0F172A] hover:border-[#0F172A]"
              }`}
              style={{ minHeight: 40 }}
            >
              <span className="flex gap-0.5">
                {t.swatch.map((c) => (
                  <span key={c} className="w-3 h-3 rounded-sm border border-white/40" style={{ background: c }}/>
                ))}
              </span>
              {t.lbl}
            </button>
          );})}
        </div>
      </div>
      <DigestCadenceCard/>
      <ApproverDelegatesCard/>
      <DelegateRenewalAnalyticsCard/>
      <div className="flex justify-end">
        <button data-testid="settings-save-btn" onClick={save} disabled={saving} className="btn-primary w-full sm:w-auto justify-center">
          <Save size={14}/> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function DigestCadenceCard() {
  const { active } = useCompany();
  const [cadence, setCadence] = useState("monthly");
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!active) return;
    api.get("/credit-notes/digest/settings")
      .then((r) => { setCadence(r.data.cadence || "monthly"); setSaved(r.data); })
      .catch(() => {});
  }, [active]);

  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.put("/credit-notes/digest/settings", { cadence: next });
      setCadence(data.cadence);
      toast.success(`Digest cadence set to ${data.cadence}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-surface p-5" data-testid="digest-cadence-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-[#B45309]/10 flex items-center justify-center shrink-0">
          <svg className="w-5 h-5 text-[#B45309]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
        <div className="flex-1">
          <div className="overline mb-1">Refund Digest</div>
          <h3 className="font-semibold text-[#0F172A] mb-1">Send Rate</h3>
          <p className="text-sm text-[#64748B]">
            How often the workspace owner receives an automated summary of every credit note issued.
          </p>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        {["weekly", "monthly", "quarterly"].map((opt) => (
          <button
            key={opt}
            data-testid={`digest-cadence-${opt}`}
            disabled={busy}
            onClick={() => save(opt)}
            className={`px-4 py-2 rounded-md text-sm font-semibold border transition ${
              cadence === opt
                ? "bg-[#0F172A] text-white border-[#0F172A]"
                : "bg-white text-[#0F172A] border-[#E2E8F0] hover:border-[#0F172A]"
            }`}
            style={{ minHeight: 40 }}
          >
            {opt.charAt(0).toUpperCase() + opt.slice(1)}
          </button>
        ))}
      </div>
      {saved?.last_sent_at && (
        <div className="mt-3 text-xs text-[#64748B]">
          Last digest sent: <span className="font-mono-data text-[#0F172A]">
            {new Date(saved.last_sent_at).toLocaleString()}
          </span>{saved.last_period && <> · period <span className="font-mono-data text-[#0F172A]">{saved.last_period}</span></>}
        </div>
      )}
    </div>
  );
}

function ApproverDelegatesCard() {
  const { active } = useCompany();
  const [me, setMe] = useState(null);
  const [members, setMembers] = useState([]);
  const [delegates, setDelegates] = useState({}); // { user_id: expires_at | null | "" }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const meRes = await api.get("/auth/me");
        setMe(meRes.data);
        if (meRes.data.role !== "super_admin" || !active) return;
        const [mRes, dRes] = await Promise.all([
          api.get(`/companies/${active.id}/members`),
          api.get(`/approvals/delegates/${active.id}`),
        ]);
        setMembers(mRes.data.members || mRes.data || []);
        const map = {};
        for (const d of (dRes.data.delegates || [])) {
          // Slice ISO datetime → yyyy-mm-dd for the date input.
          map[d.id] = d.expires_at ? d.expires_at.slice(0, 10) : "";
        }
        setDelegates(map);
      } catch (_e) { /* ignore */ }
    })();
  }, [active]);

  if (!me || me.role !== "super_admin") return null;

  const commit = async (nextMap) => {
    setBusy(true);
    try {
      const payload = {
        delegates: Object.entries(nextMap).map(([uid, exp]) => ({
          user_id: uid, expires_at: exp || null,
        })),
      };
      await api.put(`/approvals/delegates/${active.id}`, payload);
      toast.success("Delegates updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggle = (uid) => {
    const next = { ...delegates };
    if (uid in next) delete next[uid]; else next[uid] = "";
    setDelegates(next);
    commit(next);
  };

  const setExpiry = (uid, val) => {
    const next = { ...delegates, [uid]: val };
    setDelegates(next);
    commit(next);
  };

  const today = new Date().toISOString().slice(0, 10);
  const isExpired = (val) => val && val < today;

  return (
    <div className="card-surface p-5 border-l-4 border-l-[#B45309]" data-testid="approver-delegates-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-[#B45309]/10 flex items-center justify-center shrink-0">
          <svg className="w-5 h-5 text-[#B45309]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
        <div className="flex-1">
          <div className="overline mb-1">Approval Chain · Super Admin</div>
          <h3 className="font-semibold text-[#0F172A] mb-1">Approver Delegates</h3>
          <p className="text-sm text-[#64748B]">
            Designated members can approve refunds, invoices and POs on this company&apos;s behalf.
            Set an <strong>Auto-Expiry Date</strong> to make the authorization temporary — leave blank for permanent.
          </p>
        </div>
      </div>
      {members.length === 0 ? (
        <div className="text-sm text-[#64748B]">No members to designate yet.</div>
      ) : (
        <div className="space-y-2">
          {members.map((m) => {
            const uid = m.user_id || m.id;
            const isDelegate = uid in delegates;
            const exp = delegates[uid] || "";
            const expired = isExpired(exp);
            return (
              <div
                key={uid}
                data-testid={`delegate-row-${uid}`}
                className={`flex items-center gap-3 p-3 rounded-md border ${expired ? "border-[#F87171] bg-[#FEF2F2]" : "border-[#E2E8F0]"} hover:border-[#0F172A]`}
              >
                <input
                  type="checkbox"
                  disabled={busy}
                  checked={isDelegate}
                  onChange={() => toggle(uid)}
                  data-testid={`delegate-toggle-${uid}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#0F172A]">{m.name || m.email}</div>
                  <div className="text-xs text-[#64748B] font-mono-data">{m.email} · {m.role}</div>
                </div>
                {isDelegate && (
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] uppercase tracking-wider text-[#64748B]" htmlFor={`exp-${uid}`}>Auto-Expiry</label>
                    <input
                      id={`exp-${uid}`}
                      type="date"
                      value={exp}
                      min={today}
                      onChange={(e) => setExpiry(uid, e.target.value)}
                      disabled={busy}
                      data-testid={`delegate-expiry-${uid}`}
                      className="input-clean text-xs font-mono-data"
                      style={{ width: 150 }}
                    />
                    {expired ? (
                      <span className="chip text-[10px] bg-[#FEE2E2] text-[#991B1B]">Expired</span>
                    ) : (
                      <span className="chip chip-success text-[10px]">Authorized</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DelegateRenewalAnalyticsCard() {
  const { active } = useCompany();
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const meRes = await api.get("/auth/me");
        setMe(meRes.data);
        if (meRes.data.role !== "super_admin" || !active) { setLoading(false); return; }
        const { data } = await api.get("/approvals/delegate-renew/analytics?days=90");
        setData(data);
      } catch (_e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [active]);

  if (loading || !me || me.role !== "super_admin") return null;
  if (!data) return null;

  return (
    <div className="card-surface p-5" data-testid="delegate-renewal-analytics-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[#0F172A]/10 flex items-center justify-center shrink-0">
          <svg className="w-5 h-5 text-[#0F172A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"/>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"/>
          </svg>
        </div>
        <div className="flex-1">
          <div className="overline mb-1">One-Click Renewal · Last 90 days</div>
          <h3 className="font-semibold text-[#0F172A] mb-1">Renewal Analytics</h3>
          <p className="text-sm text-[#64748B]">
            How often the email-driven one-click renewal saved a delegate from lapsing.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="rounded-md border border-[#E2E8F0] p-3" data-testid="analytics-total">
          <div className="text-[10px] tracking-widest text-[#64748B] uppercase">Renewals</div>
          <div className="text-2xl font-bold font-mono-data text-[#0F172A] mt-1">{data.total_in_window}</div>
          <div className="text-[11px] text-[#94A3B8] mt-0.5">{data.total_ever} ever</div>
        </div>
        <div className="rounded-md border border-[#E2E8F0] p-3" data-testid="analytics-expired-saved">
          <div className="text-[10px] tracking-widest text-[#64748B] uppercase">Expired · Saved</div>
          <div className="text-2xl font-bold font-mono-data text-[#B45309] mt-1">{data.expired_saved_in_window}</div>
          <div className="text-[11px] text-[#94A3B8] mt-0.5">already lapsed at click-time</div>
        </div>
        <div className="rounded-md border border-[#E2E8F0] p-3" data-testid="analytics-avoidance-rate">
          <div className="text-[10px] tracking-widest text-[#64748B] uppercase">Avoidance Rate</div>
          <div className="text-2xl font-bold font-mono-data text-[#16A34A] mt-1">
            {data.expiry_avoidance_rate_pct}%
          </div>
          <div className="text-[11px] text-[#94A3B8] mt-0.5">renewed before expiry</div>
        </div>
        <div className="rounded-md border border-[#E2E8F0] p-3" data-testid="analytics-avg-months">
          <div className="text-[10px] tracking-widest text-[#64748B] uppercase">Avg · Months Granted</div>
          <div className="text-2xl font-bold font-mono-data text-[#0F172A] mt-1">{data.avg_months_granted}</div>
          <div className="text-[11px] text-[#94A3B8] mt-0.5">per renewal</div>
        </div>
      </div>
      {data.recent && data.recent.length > 0 && (
        <div>
          <div className="text-[10px] tracking-widest text-[#64748B] uppercase mb-2">Recent activity</div>
          <ul className="divide-y divide-[#E2E8F0] rounded-md border border-[#E2E8F0]" data-testid="analytics-recent-list">
            {data.recent.slice(0, 5).map((r, i) => (
              <li key={i} className="flex items-center justify-between px-3 py-2 text-xs" data-testid={`analytics-recent-${i}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-[#0F172A] font-medium truncate">{r.email || "—"}</div>
                  <div className="text-[#94A3B8] font-mono-data">
                    {r.at ? new Date(r.at).toLocaleString() : ""}
                    {r.was_expired && (
                      <span className="ml-2 chip text-[10px] bg-[#FEE2E2] text-[#991B1B]">rescued lapse</span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono-data text-[#0F172A]">+{r.months} mo</div>
                  <div className="text-[10px] text-[#94A3B8] font-mono-data">→ {(r.new_expires_at || "").slice(0, 10)}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(!data.recent || data.recent.length === 0) && (
        <div className="text-sm text-[#64748B] italic" data-testid="analytics-empty">
          No one-click renewals yet in the last {data.window_days} days.
        </div>
      )}
    </div>
  );
}

function TeamTab() {
  const { active } = useCompany();
  const [members, setMembers] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("readonly");
  const [inviting, setInviting] = useState(false);

  const load = async () => {
    if (!active) return;
    const { data } = await api.get(`/companies/${active.id}/members`);
    setMembers(data.members || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active]);

  const invite = async () => {
    if (!email) return;
    setInviting(true);
    try {
      const { data } = await api.post(`/companies/${active.id}/invite`, { email, role });
      toast.success(data.added ? "Member added" : "Invite created");
      if (data.invite_url) {
        await navigator.clipboard.writeText(data.invite_url).catch(() => {});
        toast.info("Invite URL copied to clipboard");
      }
      setEmail("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setInviting(false); }
  };

  const updateRole = async (id, newRole) => {
    try { await api.put(`/companies/${active.id}/members/${id}`, { role: newRole }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this member?")) return;
    try { await api.delete(`/companies/${active.id}/members/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="settings-team-tab">
      <div className="card-surface p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><UserPlus size={16}/> Invite Member</h3>
        <div className="flex flex-wrap gap-2">
          <input data-testid="invite-email" className="input-clean flex-1 min-w-[240px]" placeholder="email@example.com" value={email} onChange={(e) => setEmail(e.target.value)}/>
          <select data-testid="invite-role" className="input-clean" value={role} onChange={(e) => setRole(e.target.value)} style={{ width: 160 }}>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button data-testid="invite-send-btn" onClick={invite} disabled={inviting} className="btn-primary">
            {inviting ? "Sending…" : "Invite"}
          </button>
        </div>
      </div>
      <div className="card-surface p-4">
        <h3 className="font-semibold mb-3">Members</h3>
        <table className="table-clean">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th></th></tr></thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} data-testid={`member-row-${m.id}`}>
                <td className="font-medium">{m.name}</td>
                <td>{m.email}</td>
                <td>
                  <select data-testid={`member-role-${m.id}`} className="input-clean text-xs" value={m.role} onChange={(e) => updateRole(m.id, e.target.value)}>
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td><button onClick={() => remove(m.id)} className="text-[#DC2626]"><Trash2 size={14}/></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FxTab() {
  const { active } = useCompany();
  const [rates, setRates] = useState([]);
  const [f, setF] = useState({ from: "USD", to: active?.base_currency || "INR", rate: "" });
  const [fetching, setFetching] = useState(false);

  const load = async () => {
    if (!active) return;
    const { data } = await api.get("/exchange-rates");
    setRates(data.rates || []);
  };
  useEffect(() => { load(); }, [active]);

  const save = async () => {
    if (!f.rate) { toast.error("Rate required"); return; }
    try {
      await api.post("/exchange-rates", { from: f.from, to: f.to, rate: parseFloat(f.rate) });
      toast.success("Rate saved"); setF({ ...f, rate: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const fetchLive = async () => {
    setFetching(true);
    try {
      const { data } = await api.post(`/exchange-rates/fetch-save?from=${f.from}&to=${f.to}`);
      toast.success(`Fetched ${data.rate.toFixed(4)}`); load();
    } catch (e) { toast.error("Upstream FX unavailable"); }
    finally { setFetching(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/exchange-rates/${id}`); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="settings-fx-tab">
      <div className="card-surface p-4">
        <h3 className="font-semibold mb-3">Add Exchange Rate</h3>
        <div className="flex flex-wrap gap-2 items-end">
          <div><label className="label-field">From</label>
            <input className="input-clean font-mono-data uppercase" maxLength={3} value={f.from} onChange={(e) => setF({ ...f, from: e.target.value.toUpperCase() })} style={{ width: 80 }}/></div>
          <div><label className="label-field">To</label>
            <input className="input-clean font-mono-data uppercase" maxLength={3} value={f.to} onChange={(e) => setF({ ...f, to: e.target.value.toUpperCase() })} style={{ width: 80 }}/></div>
          <div><label className="label-field">Rate</label>
            <input className="input-clean font-mono-data" type="number" step="0.0001" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} style={{ width: 140 }}/></div>
          <button data-testid="fx-save-manual" onClick={save} className="btn-primary">Save Manual</button>
          <button data-testid="fx-fetch-live" onClick={fetchLive} disabled={fetching} className="btn-secondary">
            <RefreshCw size={14} className={fetching ? "animate-spin" : ""}/> Fetch Live
          </button>
        </div>
      </div>
      <div className="card-surface p-4">
        <h3 className="font-semibold mb-3">Recorded Rates</h3>
        {rates.length === 0 ? <p className="text-sm text-[#64748B]">No rates recorded.</p> : (
          <table className="table-clean">
            <thead><tr><th>Date</th><th>From</th><th>To</th><th className="text-right">Rate</th><th>Source</th><th></th></tr></thead>
            <tbody>{rates.map((r) => (
              <tr key={r.id} data-testid={`fx-row-${r.id}`}>
                <td className="font-mono-data">{r.date}</td>
                <td className="font-mono-data font-semibold">{r.from}</td>
                <td className="font-mono-data font-semibold">{r.to}</td>
                <td className="text-right font-mono-data">{Number(r.rate).toFixed(4)}</td>
                <td><span className={`chip chip-${r.source === "auto" ? "info" : "neutral"}`}>{r.source}</span></td>
                <td><button onClick={() => remove(r.id)} className="text-[#DC2626]"><Trash2 size={14}/></button></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function PasskeysTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [label, setLabel] = useState("");
  const supported = isPasskeySupported();

  const load = async () => {
    setLoading(true);
    try { setItems(await listPasskeys()); }
    catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onAdd = async () => {
    if (!supported) { toast.error("Passkeys aren't supported on this browser"); return; }
    setRegistering(true);
    try {
      const defaultLabel = label.trim() || `Passkey · ${new Date().toLocaleDateString()}`;
      await registerPasskey({ label: defaultLabel });
      toast.success("Passkey registered");
      setLabel("");
      load();
    } catch (err) {
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "AbortError") {
        toast.error("Passkey creation was cancelled");
      } else {
        toast.error(err?.response?.data?.detail || err?.message || "Failed to register passkey");
      }
    } finally { setRegistering(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this passkey? You'll need another sign-in method to log back in.")) return;
    try { await deletePasskey(id); toast.success("Passkey removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="settings-passkeys-tab">
      <div className="card-surface p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-2">
          <Fingerprint size={18} className="text-[#FF5500]"/>
          <h3 className="font-semibold text-base">Registered Devices · Passkeys & Biometrics</h3>
        </div>
        <p className="text-sm text-[#4B5563] mb-4">
          Register your device&apos;s Face ID, Touch ID, Windows Hello or fingerprint to sign in
          without typing your password. Passkeys are stored securely on this device (or in your
          Apple / Google keychain) and can&apos;t be phished.
        </p>
        {!supported ? (
          <div className="chip chip-warn" data-testid="passkey-unsupported">
            This browser doesn&apos;t support passkeys yet — try Safari on iOS or Chrome on Android/desktop.
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="passkey-label-input"
              className="input-clean flex-1"
              placeholder="Optional label (e.g. iPhone 15 · Face ID)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={120}
            />
            <button
              data-testid="passkey-add-btn"
              onClick={onAdd}
              disabled={registering}
              className="btn-primary sm:w-auto justify-center"
            >
              {registering ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>}
              Register this device
            </button>
          </div>
        )}
      </div>

      <div className="card-surface p-4 sm:p-6">
        <h3 className="font-semibold text-base mb-3">Your registered devices</h3>
        {loading ? (
          <div className="text-sm text-[#64748B]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-[#64748B]" data-testid="passkeys-empty">
            No devices registered yet. Add this device to enable fast, phish-resistant biometric sign-in on your next visit.
          </div>
        ) : (
          <ul className="divide-y divide-[#E2E8F0]">
            {items.map((p) => (
              <li key={p.id} className="py-3 flex items-center gap-3" data-testid={`passkey-row-${p.id}`}>
                <div className="w-9 h-9 rounded-lg bg-[#FFEDE0] flex items-center justify-center shrink-0">
                  <Fingerprint size={18} className="text-[#FF5500]"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#0F172A] truncate">{p.label}</div>
                  <div className="text-[11px] text-[#64748B] font-mono-data truncate">
                    Added {p.created_at ? new Date(p.created_at).toLocaleString() : "—"}
                    {p.last_used_at && (
                      <> · Last used {new Date(p.last_used_at).toLocaleString()}</>
                    )}
                  </div>
                </div>
                <button
                  data-testid={`passkey-delete-${p.id}`}
                  onClick={() => onDelete(p.id)}
                  className="p-2 text-[#DC2626] hover:bg-[#FEE2E2] rounded-md"
                  aria-label="Remove passkey"
                  style={{ minWidth: 40, minHeight: 40 }}
                >
                  <Trash2 size={16}/>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Branding tab — Company logo + digital Stamp & Signature
// Both are stored as base64 data URLs on the company doc and are
// auto-embedded into every Invoice / Receipt / Quotation / SO / PO PDF.
// ============================================================
const MAX_UPLOAD_BYTES = 600 * 1024; // 600 KB before base64 expansion

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(file);
  });
}

function AssetUploader({ label, hint, value, kind, onUpload, onClear, saving, testid }) {
  const inputRef = React.useRef(null);
  const pick = () => inputRef.current?.click();
  const onChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error("Please choose a PNG, JPG or WebP image");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(`Image is too large (max ${Math.round(MAX_UPLOAD_BYTES / 1024)} KB)`);
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      await onUpload(dataUrl);
    } catch (err) {
      toast.error("Failed to read image");
    }
  };
  return (
    <div className="card-surface p-4 sm:p-6" data-testid={`branding-${kind}-card`}>
      <div className="flex items-center gap-2 mb-1">
        {kind === "logo" ? <ImageIcon size={18} className="text-[#1E3A8A]"/> : <PenLine size={18} className="text-[#FF5500]"/>}
        <h3 className="font-semibold text-base">{label}</h3>
      </div>
      <p className="text-sm text-[#4B5563] mb-4">{hint}</p>
      <div className="flex flex-col sm:flex-row gap-4 items-start">
        <div
          className="w-full sm:w-64 h-32 rounded-lg border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC] flex items-center justify-center overflow-hidden shrink-0"
          data-testid={`branding-${kind}-preview`}
        >
          {value ? (
            <img alt="Preview" src={value} className="max-h-full max-w-full object-contain p-2"/>
          ) : (
            <div className="text-xs text-[#94A3B8] flex flex-col items-center gap-1">
              <ImageIcon size={22}/>
              <span>No image uploaded</span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <input
            ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp"
            className="hidden" onChange={onChange}
            data-testid={testid || `branding-${kind}-input`}
          />
          <button
            onClick={pick}
            disabled={saving}
            className="btn-primary w-full sm:w-auto justify-center"
            data-testid={`branding-${kind}-upload-btn`}
          >
            <Upload size={14}/> {value ? "Replace image" : "Upload image"}
          </button>
          {value && (
            <button
              onClick={onClear}
              disabled={saving}
              className="btn-ghost w-full sm:w-auto justify-center text-[#DC2626]"
              data-testid={`branding-${kind}-clear-btn`}
            >
              <Trash2 size={14}/> Remove image
            </button>
          )}
          <p className="text-[11px] text-[#94A3B8]">
            PNG / JPG / WebP · Max 600 KB · Transparent PNG works best for signatures.
          </p>
        </div>
      </div>
    </div>
  );
}

function BrandingTab() {
  const { active, refresh } = useCompany();
  const [b, setB] = useState({
    logo_data_url: null, signature_data_url: null,
    signatory_name: "", signatory_title: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!active) return;
    // Pull fresh copy in case an admin updated on another device.
    api.get(`/companies/${active.id}/branding`)
      .then((r) => setB({
        logo_data_url: r.data.logo_data_url || null,
        signature_data_url: r.data.signature_data_url || null,
        signatory_name: r.data.signatory_name || "",
        signatory_title: r.data.signatory_title || "",
      }))
      .catch(() => {});
  }, [active]);

  const put = async (patch) => {
    if (!active) return;
    setSaving(true);
    try {
      await api.put(`/companies/${active.id}/branding`, patch);
      setB((prev) => ({ ...prev, ...patch }));
      toast.success("Branding updated");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update branding");
    } finally { setSaving(false); }
  };

  const clearAsset = async (asset) => {
    if (!active) return;
    if (!window.confirm(`Remove ${asset === "logo" ? "company logo" : "stamp & signature"}?`)) return;
    setSaving(true);
    try {
      await api.delete(`/companies/${active.id}/branding/${asset}`);
      setB((prev) => ({
        ...prev,
        [asset === "logo" ? "logo_data_url" : "signature_data_url"]: null,
      }));
      toast.success("Removed");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    } finally { setSaving(false); }
  };

  const saveSignatory = async () => {
    await put({
      signatory_name: b.signatory_name || "",
      signatory_title: b.signatory_title || "",
    });
  };

  if (!active) return null;
  return (
    <div className="space-y-4" data-testid="settings-branding-tab">
      <AssetUploader
        kind="logo"
        label="Company Logo"
        hint="Appears at the top-left of every PDF (invoice, receipt, quotation, sales/purchase order). Recommended: 240 × 120 px transparent PNG."
        value={b.logo_data_url}
        onUpload={(dataUrl) => put({ logo_data_url: dataUrl })}
        onClear={() => clearAsset("logo")}
        saving={saving}
      />
      <AssetUploader
        kind="signature"
        label="Digital Stamp & Signature"
        hint="Auto-aligned at the bottom-right of every professional PDF (Invoices, Receipts, Purchase Orders). Upload a scan or transparent PNG of your seal + signature."
        value={b.signature_data_url}
        onUpload={(dataUrl) => put({ signature_data_url: dataUrl })}
        onClear={() => clearAsset("signature")}
        saving={saving}
      />
      <div className="card-surface p-4 sm:p-6" data-testid="branding-signatory-card">
        <h3 className="font-semibold text-base mb-1">Authorized Signatory</h3>
        <p className="text-sm text-[#4B5563] mb-4">
          Printed beneath the signature line on all PDFs. Leave blank to show
          just &quot;Authorized Signatory&quot;.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="label-field">Full Name</label>
            <input
              data-testid="branding-signatory-name"
              className="input-clean"
              value={b.signatory_name || ""}
              placeholder="e.g. Rajesh Kumar"
              onChange={(e) => setB({ ...b, signatory_name: e.target.value })}
            />
          </div>
          <div>
            <label className="label-field">Designation / Title</label>
            <input
              data-testid="branding-signatory-title"
              className="input-clean"
              value={b.signatory_title || ""}
              placeholder="e.g. Director · Authorized Signatory"
              onChange={(e) => setB({ ...b, signatory_title: e.target.value })}
            />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button
            data-testid="branding-signatory-save-btn"
            onClick={saveSignatory}
            disabled={saving}
            className="btn-primary w-full sm:w-auto justify-center"
          >
            <Save size={14}/> {saving ? "Saving…" : "Save Signatory"}
          </button>
        </div>
      </div>
    </div>
  );
}


export default function Settings() {
  const [tab, setTab] = useState("company");
  return (
    <div className="space-y-4" data-testid="settings-page">
      <div>
        <div className="overline mb-1">Settings</div>
        <h1 className="text-2xl font-bold text-[#0F172A]">Workspace Settings</h1>
      </div>
      <div className="flex gap-1 sm:gap-2 border-b border-[#E2E8F0] overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
        {[
          ["company", "Company"], ["branding", "Branding"], ["team", "Team"],
          ["fx", "Exchange Rates"], ["approvals", "Approval Limits"],
          ["passkeys", "Registered Devices"], ["about", "About"],
        ].map(([key, lbl]) => (
          <button
            key={key} data-testid={`settings-tab-${key}`}
            onClick={() => setTab(key)}
            className={`px-3 sm:px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap shrink-0 ${tab === key ? "border-[#FF5500] text-[#FF5500]" : "border-transparent text-[#64748B]"}`}
            style={{ minHeight: 44 }}
          >
            {lbl}
          </button>
        ))}
      </div>
      {tab === "company" && <CompanyTab />}
      {tab === "branding" && <BrandingTab />}
      {tab === "team" && <TeamTab />}
      {tab === "fx" && <FxTab />}
      {tab === "approvals" && <ApprovalsSettingsTab />}
      {tab === "passkeys" && <PasskeysTab />}
      {tab === "about" && <AboutTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AboutTab — build info + Reset App State kill switch
// ---------------------------------------------------------------------------
function AboutTab() {
  const [busy, setBusy] = useState(false);
  const [swInfo, setSwInfo] = useState(null);
  const [buildTag, setBuildTag] = useState("");

  useEffect(() => {
    (async () => {
      try {
        if ("serviceWorker" in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          const cacheNames = await caches.keys();
          const shell = cacheNames.find((n) => n.startsWith("serp-shell-"));
          setSwInfo({
            registered: regs.length,
            state: regs[0]?.active?.state,
            scope: regs[0]?.scope,
            cache_names: cacheNames,
          });
          if (shell) setBuildTag(shell.replace("serp-shell-", ""));
        }
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  const reset = async () => {
    const ok = window.confirm(
      "This will unregister the app's service worker, clear the local cache, and reload the page. Any unsaved form data will be lost. Continue?"
    );
    if (!ok) return;
    setBusy(true);
    try {
      // 1) Ask the SW to purge + unregister itself (works even if we lose focus).
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const r of regs) {
          try { r.active?.postMessage({ type: "UNREGISTER" }); } catch (_e) { /* ignore */ }
        }
      }
      // 2) Belt-and-braces: nuke caches from the client directly.
      if (window.caches) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      // 3) Force-drop every service worker regardless of the message result.
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      // 4) Purge non-auth local state so a stale token can't survive.
      try {
        Object.keys(localStorage)
          .filter((k) => !["serp_token", "serp_user"].includes(k))
          .forEach((k) => localStorage.removeItem(k));
      } catch (_e) { /* ignore */ }
      toast.success("App state reset — reloading…");
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error("Reset failed — try Ctrl+Shift+R to hard refresh");
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="settings-about-tab">
      <div className="card-surface p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[#1E3A8A]/10 flex items-center justify-center shrink-0">
            <Info size={18} className="text-[#1E3A8A]"/>
          </div>
          <div>
            <div className="overline mb-1">About this Workspace</div>
            <h3 className="font-semibold text-[#0F172A]">SERP — Global Enterprise ERP</h3>
            <p className="text-sm text-[#64748B]">
              Tally-grade double-entry accounting with multi-country GST/VAT localization,
              Stripe-integrated invoicing, and audit-ready credit notes.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm border-t border-[#E2E8F0] pt-4">
          <div>
            <div className="overline mb-1">Build</div>
            <div className="font-mono-data text-[#0F172A]" data-testid="about-build-tag">
              {buildTag || "—"}
            </div>
          </div>
          <div>
            <div className="overline mb-1">Service Worker</div>
            <div className="font-mono-data text-[#0F172A]" data-testid="about-sw-state">
              {swInfo?.registered ? `${swInfo.state || "unknown"} · ${swInfo.registered} registration${swInfo.registered > 1 ? "s" : ""}` : "not registered"}
            </div>
          </div>
        </div>
      </div>

      <div className="card-surface p-5 border-l-4 border-l-[#B45309]">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-[#B45309]/10 flex items-center justify-center shrink-0">
            <RotateCw size={18} className="text-[#B45309]"/>
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-[#0F172A] mb-1">Reset App State</h3>
            <p className="text-sm text-[#64748B]">
              Unregisters the service worker, clears the local cache and offline reports,
              then reloads the page. Use this if the app looks broken or shows a stale version.
              Your data on the server is NOT touched — only what your browser stored locally.
            </p>
          </div>
        </div>
        <button
          data-testid="reset-app-state-btn"
          onClick={reset}
          disabled={busy}
          className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#B45309] hover:bg-[#92400E] disabled:opacity-50"
          style={{ minHeight: 44 }}
        >
          {busy ? <Loader2 size={14} className="animate-spin"/> : <RotateCw size={14}/>}
          {busy ? "Resetting…" : "Reset & Reload"}
        </button>
      </div>
    </div>
  );
}
